import { useCallback, useState } from 'react';
import { isEqual } from '../../../utils/helpers';
import { assembleChartTitle, calcInfoValues, calcYDomain, dataProcessingPipeline } from '../helpers';
import type { InfoValues, ParsedDataProps, XDomain, XRange, YDomain } from '../types';
import { HighlightLinePresets } from '../types';

export interface UsePerformanceHandlersParams {
  detailXDomain: XDomain | null;
  parsedData: ParsedDataProps[];
  xRange: XRange;
  hasBenchmark: boolean;
  reportCurrency: string;
  dataHeader: string;
  currentLanguage: string;
  refetch: any;
  setDataLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setFetchFailure: React.Dispatch<React.SetStateAction<boolean>>;
  setPreparedDetailData: React.Dispatch<React.SetStateAction<ParsedDataProps[]>>;
  setDetailXDomain: React.Dispatch<React.SetStateAction<XDomain | null>>;
  setDetailYDomain: React.Dispatch<React.SetStateAction<YDomain | null>>;
  setTitle: React.Dispatch<React.SetStateAction<string>>;
  setPerformanceInfo: React.Dispatch<React.SetStateAction<InfoValues>>;
}

export interface UsePerformanceHandlersReturn {
  menuExpanded: boolean;
  currencySelectionVisible: boolean;
  highlightLine: HighlightLinePresets;
  chartIsWide: boolean;
  toggleMenu: () => void;
  showCurrencySelection: () => void;
  hideCurrencySelection: () => void;
  hoverValue: (value: HighlightLinePresets) => void;
  leaveValue: () => void;
  changeCurrency: (currency: string) => void;
  updateDetailDataHandler: (newDetailXDomain: XDomain) => Promise<void>;
  toggleChartWidth: () => void;
}

export function usePerformanceHandlers({
  detailXDomain,
  parsedData,
  xRange,
  hasBenchmark,
  reportCurrency,
  dataHeader,
  currentLanguage,
  refetch,
  setDataLoaded,
  setFetchFailure,
  setPreparedDetailData,
  setDetailXDomain,
  setDetailYDomain,
  setTitle,
  setPerformanceInfo,
}: UsePerformanceHandlersParams): UsePerformanceHandlersReturn {
  // UI state
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [currencySelectionVisible, setCurrencySelectionVisible] = useState(false);
  const [highlightLine, setHighlightLine] = useState<HighlightLinePresets>(HighlightLinePresets.NOTHING);
  const [chartIsWide, setChartIsWide] = useState(false);

  const toggleMenu = useCallback(() => {
    setMenuExpanded((prev) => !prev);
  }, []);

  const showCurrencySelection = useCallback(() => {
    setCurrencySelectionVisible(true);
  }, []);

  const hideCurrencySelection = useCallback(() => {
    setCurrencySelectionVisible(false);
  }, []);

  const hoverValue = useCallback((value: HighlightLinePresets) => {
    setHighlightLine(value);
  }, []);

  const leaveValue = useCallback(() => {
    setHighlightLine(HighlightLinePresets.NOTHING);
  }, []);

  const changeCurrency = useCallback(
    (currency: string) => {
      setDataLoaded(false);

      refetch(
        {
          loadData: true,
          reportcurrency: currency,
        },
        {
          fetchPolicy: 'network-only',
          onComplete: (error: any) => {
            if (error) {
              setFetchFailure(true);
            } else {
              setDataLoaded(true);
            }
          },
        },
      );

      hideCurrencySelection();
    },
    [refetch, hideCurrencySelection, setDataLoaded, setFetchFailure],
  );

  const updateDetailDataHandler = useCallback(
    async (newDetailXDomain: XDomain) => {
      if (!isEqual(newDetailXDomain, detailXDomain)) {
        const { processedData: processedDetailData, verifiedXDomain: verifiedDetailXDomain } = await dataProcessingPipeline(parsedData, newDetailXDomain, xRange, hasBenchmark);

        const newDetailYDomain = calcYDomain(processedDetailData, hasBenchmark);

        setPreparedDetailData(processedDetailData);
        setDetailXDomain(verifiedDetailXDomain);
        setDetailYDomain(newDetailYDomain);
        setTitle(assembleChartTitle(reportCurrency, dataHeader, verifiedDetailXDomain, currentLanguage));
        setPerformanceInfo(calcInfoValues(processedDetailData, verifiedDetailXDomain));
      }
    },
    [detailXDomain, parsedData, xRange, hasBenchmark, reportCurrency, dataHeader, currentLanguage, setPreparedDetailData, setDetailXDomain, setDetailYDomain, setTitle, setPerformanceInfo],
  );

  const toggleChartWidth = useCallback(() => {
    setChartIsWide((prev) => !prev);
  }, []);

  return {
    menuExpanded,
    currencySelectionVisible,
    highlightLine,
    chartIsWide,
    toggleMenu,
    showCurrencySelection,
    hideCurrencySelection,
    hoverValue,
    leaveValue,
    changeCurrency,
    updateDetailDataHandler,
    toggleChartWidth,
  };
}
